'use client';

import React, { useEffect, useState } from 'react';

// Simple button component since we can't find the UI button
const Button = ({
  children,
  variant,
  onClick
}: {
  children: React.ReactNode;
  variant?: 'outline' | 'default';
  onClick?: () => void;
}) => (
  <button
    className={`px-4 py-2 rounded-md font-medium ${
      variant === 'outline'
        ? 'border border-gray-300 text-gray-700'
        : 'bg-blue-600 text-white hover:bg-blue-700'
    }`}
    onClick={onClick}
  >
    {children}
  </button>
);

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

export function ErrorBoundary({ children }: ErrorBoundaryProps) {
  const [hasError, setHasError] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [errorInfo, setErrorInfo] = useState<React.ErrorInfo | null>(null);

  useEffect(() => {
    // Add error event listener for uncaught errors
    const handleError = (event: ErrorEvent) => {
      console.error('Uncaught error:', event.error);
      setHasError(true);
      setError(event.error);
    };

    // Add unhandled rejection listener for promise errors
    const handleRejection = (event: PromiseRejectionEvent) => {
      console.error('Unhandled rejection:', event.reason);
      setHasError(true);
      setError(new Error(event.reason?.message || 'Unhandled Promise rejection'));
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleRejection);
    };
  }, []);

  if (hasError) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4 bg-gray-100">
        <div className="w-full max-w-md p-6 bg-white rounded-lg shadow-lg">
          <h2 className="text-2xl font-bold text-red-600 mb-4">
            Something went wrong
          </h2>
          <div className="bg-gray-100 p-4 rounded mb-4 overflow-auto max-h-60">
            <p className="font-mono text-sm">{error?.message || 'Unknown error'}</p>
            {errorInfo && (
              <pre className="mt-2 text-xs overflow-auto">
                {errorInfo.componentStack}
              </pre>
            )}
          </div>
          <div className="flex justify-between">
            <Button
              variant="outline"
              onClick={() => {
                console.log('Reloading page...');
                window.location.reload();
              }}
            >
              Reload Page
            </Button>
            <Button
              onClick={() => {
                setHasError(false);
                setError(null);
                setErrorInfo(null);
              }}
            >
              Try Again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {children}
    </>
  );
}

// Class-based error boundary for catching render errors
export class ClassErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  { hasError: boolean; error: Error | null; errorInfo: React.ErrorInfo | null }
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({ errorInfo });
    console.error('Error caught by boundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center min-h-screen p-4 bg-gray-100">
          <div className="w-full max-w-md p-6 bg-white rounded-lg shadow-lg">
            <h2 className="text-2xl font-bold text-red-600 mb-4">
              Rendering Error
            </h2>
            <div className="bg-gray-100 p-4 rounded mb-4 overflow-auto max-h-60">
              <p className="font-mono text-sm">{this.state.error?.message || 'Unknown error'}</p>
              {this.state.errorInfo && (
                <pre className="mt-2 text-xs overflow-auto">
                  {this.state.errorInfo.componentStack}
                </pre>
              )}
            </div>
            <div className="flex justify-between">
              <Button
                variant="outline"
                onClick={() => {
                  console.log('Reloading page...');
                  window.location.reload();
                }}
              >
                Reload Page
              </Button>
              <Button
                onClick={() => {
                  this.setState({ hasError: false, error: null, errorInfo: null });
                }}
              >
                Try Again
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
